import { useEffectEvent } from '@floating-ui/react/utils';
import { Expand01, Minimize01, Plus } from '@untitled-ui/icons-react';
import clsx from 'clsx';
import { type FC, useCallback, useEffect, useState } from 'react';

import { ChatInput } from '@/components/chat/ChatInput';
import { ChatMessages } from '@/components/chat/ChatMessages';
import { ChatOptions } from '@/components/chat/ChatOptions';
import { DraftPromptBar } from '@/components/chat/DraftPromptBar';
import { useAgentChat } from '@/components/chat/hooks/useAgentChat';
import { useChatOptions } from '@/components/chat/hooks/useChatOptions';
import { usePageContext } from '@/components/chat/hooks/usePageContext';
import { useChatStore } from '@/components/chat/useChatStore';
import { AISidePanelHeader } from '@/components/side-panel/ai/AISidePanelHeader';
import { SidePanelContainer } from '@/components/side-panel/SidePanelContainer';
import type { SidePanelState } from '@/components/side-panel/useSidePanelStore';
import { useSidePanelStore } from '@/components/side-panel/useSidePanelStore';
import { useIsCustomerSupport } from '@/hooks/useIsCustomerSupport';

import styles from './style.module.scss';

let restartChat = false;

useSidePanelStore.subscribe(
  (state: SidePanelState, prevState: SidePanelState) => {
    if (state.key !== prevState.key) {
      restartChat = true;
    }
  }
);

export const AIChatSidePanel: FC = () => {
  const {
    clearMessages,
    clearOptions,
    setSessionId,
    setSessionInitialized,
    isLoading,
    isInitialising,
    messages,
    options,
  } = useChatStore();

  const [inputValue, setInputValue] = useState('');
  const [isDraftPrompt, setIsDraftPrompt] = useState(false);

  const isFullScreen = useSidePanelStore((state) => state.isFullScreen);
  const toggleFullScreen = useSidePanelStore((state) => state.toggleFullScreen);

  const isCustomerSupport = useIsCustomerSupport();
  // The draft-prompt toggle (AID-290) is an internal-only agent affordance. The
  // panel is agent-only, and its launcher is gated on the agent entitlement, so
  // internal-user is the only remaining condition.
  const showDraftPromptToggle = isCustomerSupport;

  const { sendAgentMessage, resetAgentSession, cancelAgentTurn } =
    useAgentChat();

  // Page/form awareness for the agent (AID-379). Snapshotted per send, not
  // subscribed — see `usePageContext`.
  const getPageContext = usePageContext();

  // Single agent sender that threads the current draft-prompt toggle state, used
  // by both typed messages and starter clicks so they always carry the same flag.
  // Only internal users see the toggle; guard so a stale flag can't leak.
  const sendAgentTurn = useCallback(
    (message: string) =>
      sendAgentMessage(
        message,
        showDraftPromptToggle && isDraftPrompt,
        getPageContext()
      ),
    [sendAgentMessage, showDraftPromptToggle, isDraftPrompt, getPageContext]
  );

  useChatOptions(sendAgentTurn);

  function handleNewChat() {
    // Abort any in-flight agent turn first so its remaining socket events can't
    // stream into the fresh chat; this settles the turn silently (no error) and
    // drops the socket (AID-331).
    cancelAgentTurn();
    clearMessages();
    // Clear the starter chips so `useChatOptions` re-picks a fresh random subset
    // off the now-empty transcript — this is what makes New chat reshuffle them.
    clearOptions();
    setSessionId(null);
    setSessionInitialized(false);
    // The agent session is server-owned per (user, org); clearing the UI alone
    // would leave the next agent turn resuming the previous conversation
    // (AID-331). Ask the gateway to start a fresh session on the next turn.
    resetAgentSession();
  }

  const handleSendMessage = async () => {
    const userMessage = inputValue.trim();
    if (!userMessage || isLoading || isInitialising) {
      return;
    }
    // Agent mode is server-session (the gateway owns it per user+org), so there
    // is no client sessionId gate to satisfy before sending.
    setInputValue(''); // Clear input only after validation passes

    await sendAgentTurn(userMessage);
  };

  const startANewChat = useEffectEvent(() => {
    handleNewChat();
  });

  useEffect(() => {
    if (restartChat) {
      startANewChat();

      restartChat = false;
    }
  }, [startANewChat]);

  return (
    <SidePanelContainer
      header={
        <AISidePanelHeader
          title={'RiskSmart Agent'}
          toolbarButtons={[
            <button
              onClick={toggleFullScreen}
              title={isFullScreen ? 'Exit full screen' : 'Full screen'}
              aria-label={isFullScreen ? 'Exit full screen' : 'Full screen'}
              key={'full-screen'}
            >
              {isFullScreen ? <Minimize01 /> : <Expand01 />}
            </button>,
            <button onClick={handleNewChat} title={'New chat'} key={'new-chat'}>
              <Plus />
            </button>,
          ]}
        ></AISidePanelHeader>
      }
      content={
        <div className={clsx(styles.chatPanel, 'overflow-y-hidden')}>
          {showDraftPromptToggle && (
            <DraftPromptBar
              checked={isDraftPrompt}
              onChange={setIsDraftPrompt}
            />
          )}
          <ChatMessages
            messages={messages}
            isLoading={isLoading}
            isInitialising={isInitialising}
          />
          <ChatOptions options={options} />
          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSend={handleSendMessage}
            isLoading={isLoading || isInitialising}
          />
        </div>
      }
    ></SidePanelContainer>
  );
};
